import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Gift, Wrench, Check, ArrowRight } from "lucide-react";
import { CHANGELOG, LATEST_VERSION, getNewEntries, ChangelogEntry } from "@/config/changelog";

const LAST_SEEN_VERSION_KEY = "wormble-last-seen-version";

interface WhatsNewModalProps {
  forceOpen?: boolean;
  onClose?: () => void;
}

export function WhatsNewModal({ forceOpen = false, onClose }: WhatsNewModalProps) {
  const [open, setOpen] = useState(false);
  const [newEntries, setNewEntries] = useState<ChangelogEntry[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (forceOpen) {
      setNewEntries(CHANGELOG);
      setOpen(true);
      return;
    }

    // Check if there are new updates to show
    const lastSeen = localStorage.getItem(LAST_SEEN_VERSION_KEY);
    
    if (!lastSeen) {
      // First visit - don't overwhelm, just mark as seen
      localStorage.setItem(LAST_SEEN_VERSION_KEY, LATEST_VERSION);
      return;
    }

    if (lastSeen !== LATEST_VERSION) {
      const entries = getNewEntries(lastSeen);
      if (entries.length > 0) {
        setNewEntries(entries);
        setOpen(true);
      }
    }
  }, [forceOpen]);

  const handleClose = () => {
    localStorage.setItem(LAST_SEEN_VERSION_KEY, LATEST_VERSION);
    setOpen(false);
    onClose?.();
  };

  const getTypeIcon = (type: ChangelogEntry['type']) => {
    switch (type) {
      case 'major': return <Gift className="h-4 w-4" />;
      case 'minor': return <Sparkles className="h-4 w-4" />;
      case 'patch': return <Wrench className="h-4 w-4" />;
    }
  };

  const getTypeBadge = (type: ChangelogEntry['type']) => {
    switch (type) {
      case 'major': return <Badge className="bg-primary text-primary-foreground">Major</Badge>;
      case 'minor': return <Badge variant="secondary">Update</Badge>;
      case 'patch': return <Badge variant="outline">Fix</Badge>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            What's New
          </DialogTitle>
          <DialogDescription>
            Here's what's been added since your last visit
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {newEntries.map((entry, index) => (
              <div key={entry.version} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(entry.type)}
                    <span className="font-semibold">{entry.title}</span>
                  </div>
                  {getTypeBadge(entry.type)}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  v{entry.version} • {new Date(entry.date).toLocaleDateString()}
                </p>

                <ul className="space-y-2">
                  {entry.highlights.map((highlight, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-correct mt-0.5 shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>

                {index < newEntries.length - 1 && (
                  <div className="border-t border-border pt-4" />
                )}
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="flex flex-col gap-3 pt-2">
          <button
            onClick={() => {
              handleClose();
              navigate('/roadmap');
            }}
            className="flex items-center justify-center gap-2 text-sm text-primary hover:underline"
          >
            See what's coming next
            <ArrowRight className="h-4 w-4" />
          </button>
          <Button onClick={handleClose} className="w-full">
            Got it!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook for triggering the modal from settings
export function useWhatsNew() {
  const [showModal, setShowModal] = useState(false);
  
  return {
    showModal,
    openWhatsNew: () => setShowModal(true),
    closeWhatsNew: () => setShowModal(false),
    WhatsNewModalComponent: showModal ? (
      <WhatsNewModal forceOpen onClose={() => setShowModal(false)} />
    ) : null
  };
}
